import { ArrowUpRight, FileUp, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import type { Expense } from "../../../lib/sales";
import { Pop, useClosePops } from "../../shell/Pop";

/**
 * The 44px column at the end of an expense row.
 *
 * This replaces three ghost icon buttons that sat in the row itself: rebill,
 * edit, delete. Only entries that can actually run are drawn - rebilling needs
 * a client on the expense and an invoice it has not produced yet, and once it
 * has produced one the useful action is the opposite, jumping to that invoice.
 */
export function ExpenseRowMenu({
  expense,
  canManage,
  onOpenInvoice,
  onBill,
  onEdit,
  onDelete,
}: {
  expense: Expense;
  canManage: boolean;
  onOpenInvoice: (invoiceId: string) => void;
  onBill: (expense: Expense) => void;
  onEdit: (expense: Expense) => void;
  onDelete: (expense: Expense) => void;
}) {
  const t = useT();
  const close = useClosePops();

  const invoiceId = expense.invoicedId;
  const canBill = canManage && expense.billable && !invoiceId && !!expense.clientId;

  // Nothing to offer: keep the track occupied so the row's columns stay aligned
  // with the head, but draw no button.
  if (!invoiceId && !canManage) return <span className="justify-self-end" />;

  function run(fn: () => void) {
    close();
    fn();
  }

  return (
    // Above the row content, so the menu takes the click rather than the row.
    <span className="relative z-[1] justify-self-end">
      <Pop
        trigger={(open) => (
          <button
            type="button"
            className={`crm-cb${open ? " is-on" : ""}`}
            title={t("common.more")}
            aria-label={t("common.more")}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <MoreHorizontal className="size-4" />
          </button>
        )}
      >
        {invoiceId ? (
          <button type="button" className="crm-mi" onClick={() => run(() => onOpenInvoice(invoiceId))}>
            <ArrowUpRight className="size-4" />
            {t("expenses.openInvoice")}
          </button>
        ) : null}

        {canBill ? (
          <button type="button" className="crm-mi" onClick={() => run(() => onBill(expense))}>
            <FileUp className="size-4" />
            {t("expenses.bill")}
          </button>
        ) : null}

        {canManage ? (
          <>
            {invoiceId || canBill ? <div className="crm-menu-sep" /> : null}
            <button type="button" className="crm-mi" onClick={() => run(() => onEdit(expense))}>
              <Pencil className="size-4" />
              {t("expenses.edit")}
            </button>
            <div className="crm-menu-sep" />
            <button type="button" className="crm-mi crm-mi-danger" onClick={() => run(() => onDelete(expense))}>
              <Trash2 className="size-4" />
              {t("common.delete")}
            </button>
          </>
        ) : null}
      </Pop>
    </span>
  );
}
